import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, X, FileText, ExternalLink, Edit2 } from "lucide-react";

const KIND_LABELS = {
  agreement: "Agreement",
  disclaimer: "Disclaimer",
  insurance: "Insurance",
  contract: "Contract",
  compliance: "Compliance",
  other: "Other",
};

const STATUS_PILL = {
  active: "bg-emerald-100 text-emerald-700",
  expired: "bg-rose-100 text-rose-700",
  pending: "bg-amber-100 text-amber-800",
};

const EMPTY = {
  title: "",
  kind: "agreement",
  url: "",
  status: "active",
  expires_at: "",
  notes: "",
};

/**
 * Shared documents page — used by all three portals (clinic, vendor, admin).
 * Stores metadata only (no file uploads in this iteration).
 */
export default function DocumentsPage({
  accent = "blue",
  eyebrow = "Resources · Documents",
  title = "Document registry",
  description = "Track agreements, disclaimers, insurance attestations, and other operational paperwork. Add a title, optional link, and expiry — Nodera will surface anything overdue.",
  ownerRoleFilter = null,
  headerSlot = null,
  footerSlot = null,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | row
  const [form, setForm] = useState(EMPTY);

  const refresh = () =>
    api
      .get("/documents")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };
  const openEdit = (row) => {
    setForm({
      title: row.title || "",
      kind: row.kind || "other",
      url: row.url || "",
      status: row.status || "active",
      expires_at: row.expires_at || "",
      notes: row.notes || "",
    });
    setEditing(row);
  };

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post("/documents", form);
        toast.success("Added");
      } else {
        await api.patch(`/documents/${editing.id}`, form);
        toast.success("Updated");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await api.delete(`/documents/${row.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const ACC = {
    blue: "bg-blue-600 hover:bg-blue-700 text-blue-700",
    emerald: "bg-emerald-600 hover:bg-emerald-700 text-emerald-700",
    violet: "bg-violet-600 hover:bg-violet-700 text-violet-700",
  }[accent];

  const visibleItems = ownerRoleFilter
    ? items.filter((row) => row.owner_role === ownerRoleFilter)
    : items;

  return (
    <div className="space-y-5" data-testid="documents-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className={`text-[11px] uppercase tracking-[0.18em] font-semibold ${ACC.split(" ").pop()}`}>
            {eyebrow}
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1">{title}</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">{description}</p>
        </div>
        <button
          onClick={openNew}
          data-testid="documents-new-btn"
          className={`inline-flex items-center gap-2 ${ACC.split(" ").slice(0, 2).join(" ")} text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm`}
        >
          <Plus className="w-4 h-4" /> Add document
        </button>
      </div>

      {headerSlot}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : visibleItems.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center"
          data-testid="documents-empty"
        >
          <FileText className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-600">
            {ownerRoleFilter
              ? `No ${ownerRoleFilter} documents tracked yet.`
              : "No documents tracked yet."}
          </div>
        </div>
      ) : (
        <div
          className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
          data-testid="documents-table"
        >
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold">Kind</th>
                <th className="text-left px-4 py-3 font-semibold">Owner</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Expires</th>
                <th className="text-left px-4 py-3 font-semibold w-32"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-slate-100 hover:bg-slate-50/50"
                  data-testid={`document-row-${row.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{row.title}</div>
                    {row.notes && (
                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{row.notes}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {KIND_LABELS[row.kind] || row.kind}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {row.owner_name || "—"}
                    {row.owner_role && (
                      <span className="ml-1 text-slate-400">· {row.owner_role}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${
                        STATUS_PILL[row.status] || STATUS_PILL.active
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {row.expires_at ? row.expires_at.split("T")[0] : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {row.url && (
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-2 py-1"
                          data-testid={`document-open-${row.id}`}
                        >
                          <ExternalLink className="w-3 h-3" /> Open
                        </a>
                      )}
                      <button
                        onClick={() => openEdit(row)}
                        className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-2 py-1"
                        data-testid={`document-edit-${row.id}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => remove(row)}
                        className="inline-flex items-center gap-1 text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-md px-2 py-1"
                        data-testid={`document-delete-${row.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {footerSlot}

      {editing && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-3"
          onClick={() => setEditing(null)}
        >
          <div
            className="bg-white w-full max-w-lg rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="document-modal"
          >
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="font-semibold text-sm text-slate-900">
                {editing === "new" ? "Add document" : `Edit · ${editing.title}`}
              </div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Title *">
                <input
                  data-testid="document-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="ipt"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Kind">
                  <select
                    value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    className="ipt"
                  >
                    {Object.entries(KIND_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Status">
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="ipt"
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="expired">Expired</option>
                  </select>
                </Field>
              </div>
              <Field label="Link (optional)">
                <input
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  className="ipt"
                  placeholder="https://"
                />
              </Field>
              <Field label="Expires (optional)">
                <input
                  type="date"
                  value={form.expires_at ? form.expires_at.split("T")[0] : ""}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="ipt"
                />
              </Field>
              <Field label="Notes">
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="ipt"
                />
              </Field>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="text-sm font-semibold text-slate-700 px-4 py-2 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={!form.title?.trim()}
                data-testid="document-save"
                className={`${ACC.split(" ").slice(0, 2).join(" ")} text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ipt {
          width: 100%;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 0.5rem 0.75rem;
          font-size: 14px;
          color: #0f172a;
          background: white;
        }
        .ipt:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59,130,246,0.15); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>
      {children}
    </label>
  );
}
